import { Badge, type BadgeVariant, Button, Card, IconTile } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Play, Workflow } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { getFlowOptions, type SpaceResourceResponse, type SpaceWorkflowSummary } from "@/api";
import { Text } from "@/components/Text";
import { spaceWorkflowPath, spaceWorkflowRunPath } from "@/features/build/workflows/flowRunPaths";
import { RunFlowDialog } from "@/features/build/workflows/RunFlowDialog";
import { formatRelativeTime } from "@/i18n/format";
import { NoAccessNote, OnlyYouBadge } from "./ItemNotes";

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  completed: "success",
  failed: "danger",
  paused: "warning",
  running: "info",
  pending: "info",
  cancelled: "neutral",
};
const STATUSES = ["completed", "failed", "paused", "running", "pending", "cancelled"] as const;

/** How the workflow's most recent run went (only that run: the card never lists its history). */
function LastRun({ workflow }: Readonly<{ workflow: SpaceWorkflowSummary }>) {
  const { t } = useTranslation("workflows", { keyPrefix: "card" });
  const status = STATUSES.find((s) => s === workflow.lastRunStatus);
  if (!workflow.lastRunAt || !status) {
    return (
      <Text variant="caption" tone="tertiary">
        {t("neverRun")}
      </Text>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge variant={STATUS_VARIANT[status]} size="compact">
        {t(`status.${status}`)}
      </Badge>
      <Text as="span" variant="caption" tone="tertiary">
        {t("lastRun", { time: formatRelativeTime(workflow.lastRunAt) })}
      </Text>
    </span>
  );
}

/** Run, from the card: loads the workflow (its default input, steps) only when asked. */
function RunFromSpace({ spaceUuid, workflow }: Readonly<{ spaceUuid: string; workflow: SpaceWorkflowSummary }>) {
  const { t } = useTranslation("workflows");
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const flow = useQuery({ ...getFlowOptions({ path: { flow_uuid: workflow.uuid } }), enabled: open });
  const runPath = useCallback(
    (flowUuid: string, runUuid: string) => spaceWorkflowRunPath(spaceUuid, flowUuid, runUuid),
    [spaceUuid],
  );
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<Play size={14} aria-hidden />}
        disabled={workflow.hasSteps === false}
        loading={open && flow.isPending}
        aria-label={t("runDialog.title", { name: workflow.name })}
        onClick={() => setOpen(true)}
      >
        {t("runs.run")}
      </Button>
      {open && flow.data && <RunFlowDialog flow={flow.data.data} open onClose={close} runPath={runPath} />}
    </>
  );
}

/**
 * A workflow on a space's page: its name (opening its runs, under the space), how its last run went, and Run.
 * `item` is the space item, when it is one (a personal space's defaults aren't).
 */
export function WorkflowCard({
  spaceUuid,
  workflow,
  item,
}: Readonly<{ spaceUuid: string; workflow: SpaceWorkflowSummary; item?: SpaceResourceResponse }>) {
  const usable = item?.canUse !== false;
  return (
    <Card size="sm" className="relative flex w-full flex-col">
      <div className="flex items-start gap-2.5">
        <IconTile size="sm">
          <Workflow size={14} />
        </IconTile>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="flex items-center gap-2">
            {usable ? (
              // Stretched over the card, so the whole card opens the workflow; Run sits above it.
              <Link
                to={spaceWorkflowPath(spaceUuid, workflow.uuid)}
                className="min-w-0 truncate rounded btn-focus-ring after:absolute after:inset-0 after:content-['']"
              >
                <Text as="span" variant="body-sm" weight="semibold">
                  {workflow.name}
                </Text>
              </Link>
            ) : (
              <Text as="span" variant="body-sm" weight="semibold" className="truncate">
                {workflow.name}
              </Text>
            )}
            {item && <OnlyYouBadge item={item} />}
          </span>
          {usable && <LastRun workflow={workflow} />}
          {item && <NoAccessNote item={item} />}
        </div>
        {usable && (
          <div className="relative z-10 shrink-0">
            <RunFromSpace spaceUuid={spaceUuid} workflow={workflow} />
          </div>
        )}
      </div>
    </Card>
  );
}
